import { api } from "@/lib/api";

/** Convert base64url VAPID public key → Uint8Array (required by
 *  pushManager.subscribe). */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/** Ask permission, subscribe this device to Web Push and register with the
 *  backend. Idempotent — safe to call on every app load. Returns
 *  { status: 'subscribed' | 'denied' | 'unsupported' | 'permission-default' | 'error', reason? }.
 */
export async function ensurePushSubscription({ requestPermission = false } = {}) {
  // Alguns webviews Android (apps embutidos no WhatsApp, Instagram, Facebook)
  // definem `serviceWorker` + `PushManager` mas NÃO o global `Notification`.
  // Checar antes de ler `.permission` evita um ReferenceError que derruba a
  // montagem do `NotifBell` (e, com ErrorBoundary, mostra a tela de erro).
  if (typeof window === "undefined"
      || !("serviceWorker" in navigator)
      || !("PushManager" in window)
      || typeof window.Notification === "undefined") {
    return { status: "unsupported" };
  }
  if (Notification.permission === "denied") return { status: "denied" };
  if (Notification.permission !== "granted") {
    if (!requestPermission) return { status: "permission-default" };
    const p = await Notification.requestPermission();
    if (p !== "granted") return { status: p === "denied" ? "denied" : "permission-default" };
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get("/notifications/vapid-public-key");
      if (!data?.public_key) return { status: "error", reason: "no-vapid" };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    // Serialise to plain JSON so pywebpush can consume it (endpoint + keys).
    const json = sub.toJSON();
    await api.post("/notifications/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
    });
    return { status: "subscribed", sub: json };
  } catch (e) {
    return { status: "error", reason: e?.message || String(e) };
  }
}

/** Unsubscribe this device from Web Push. */
export async function disablePushSubscription() {
  if (!("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      await api.post("/notifications/unsubscribe", { endpoint: json.endpoint, keys: json.keys }).catch(() => {});
      await sub.unsubscribe();
    }
  } catch {
    /* silent */
  }
}
